import React, { FC, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IVehicle } from 'modules/core/types';
import {
  colonFormatter,
  costFormatter,
  getExtraCashConversionValue,
  thousandFormatter,
  vinFormatter,
} from 'modules/core/utils';
import { Badge, Stock } from 'modules/core/components';
import { useModalState, useVehicleStatusBadgeProps } from 'modules/core/hooks';
import { ColorBadge, ColorDot } from 'modules/core/components/ColorDot/ColorDot';
import { useFormContext } from 'react-hook-form';
import { TableView } from 'modules/core/enums';
import { useDispatch, useSelector } from 'react-redux';
import { Image as ImageUI } from 'react-bootstrap';
import { useLocation } from 'react-router-dom';
import { ROUTER_PATH } from 'router/const/routerPath.const';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { setVehicleDetails } from 'store/reducers/vehicle.reducer';
import { MODAL_DIALOG_ID, STATIC_IMAGE_URLS } from 'modules/core/constants';
import { AppDispatch } from 'store/store';
import { VINSpan } from 'common/styles';
import { showNotification } from 'store/reducers/ui.reducer';
import { getUser } from 'store/selectors/auth.selector';

import {
  Card,
  CardHeader,
  CardRow,
  CardDescription,
  CardImageContainer,
  CardCheckbox,
  BadgeContainer,
  ColorBadgeWrapper,
} from './styles';

export interface IVehicleCard {
  vehicle: IVehicle;
}

export const VehicleCard: FC<IVehicleCard> = ({ vehicle, children }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { handleOpenDialog } = useModalState([MODAL_DIALOG_ID.VEHICLE_DETAILS]);
  const { t } = useTranslation();
  const badgeProps = useVehicleStatusBadgeProps(vehicle.age, vehicle.is_new, vehicle.is_sold);
  const { watch } = useFormContext();
  const { tableView } = watch();
  const { pathname } = useLocation();
  const user = useSelector(getUser);
  const { noPhoto, blackCarCard, blackCarManuallyCreatedCard } = STATIC_IMAGE_URLS.icons;

  const isImageExist = !!vehicle.full_size_photo[0];

  const isExtraCashFormatted = pathname === ROUTER_PATH.PROBABILITY_ESTIMATOR;

  const openVehicleDetailsDialog = useCallback(() => {
    dispatch(setVehicleDetails({ id: vehicle.id, title: vehicle.description }));
    handleOpenDialog();
  }, [handleOpenDialog, dispatch, vehicle]);

  const src = useMemo(() => {
    if (vehicle.manually_created) {
      return blackCarManuallyCreatedCard;
    }

    if (user?.withWholesaleInventory) {
      return isImageExist ? vehicle.full_size_photo[0] : blackCarCard;
    }

    return isImageExist ? vehicle.full_size_photo[0] : noPhoto;
  }, [
    vehicle.manually_created,
    vehicle.full_size_photo,
    isImageExist,
    blackCarCard,
    blackCarManuallyCreatedCard,
    user?.withWholesaleInventory,
    noPhoto,
  ]);

  const [imageSrc, setImageSrc] = useState<string>(src);

  const dialogTriggerProps = useMemo(
    () =>
      [noPhoto, blackCarCard, blackCarManuallyCreatedCard].includes(imageSrc)
        ? {}
        : { onClick: openVehicleDetailsDialog },
    [blackCarCard, blackCarManuallyCreatedCard, imageSrc, noPhoto, openVehicleDetailsDialog],
  );

  const handleError = () => {
    setImageSrc(STATIC_IMAGE_URLS.icons.noPhoto);
  };

  return (
    <Card key={vehicle.id}>
      <CardHeader>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <CardCheckbox>{children}</CardCheckbox>
          {vehicle.manually_created ? <div></div> : <Stock stock={vehicle.stock} />}
        </div>
        <CopyToClipboard
          text={vehicle.vin}
          onCopy={() => dispatch(showNotification({ message: t('successfullyCopied'), type: 'successful' }))}
        >
          <VINSpan title={t('components.table.vinHelpMessage')}>{vinFormatter(vehicle.vin)}</VINSpan>
        </CopyToClipboard>
      </CardHeader>
      <CardImageContainer>
        <ImageUI src={imageSrc} onError={handleError} {...dialogTriggerProps} />
        <BadgeContainer>{badgeProps && <Badge {...badgeProps} isCardView={true} />}</BadgeContainer>
        {user?.withWholesaleInventory && (vehicle.color || vehicle.color_canonical) && (
          <ColorBadgeWrapper>
            <ColorBadge canonical={vehicle.color_canonical} label={vehicle.color} size={16} />
          </ColorBadgeWrapper>
        )}
      </CardImageContainer>
      <CardDescription>{vehicle.description}</CardDescription>
      {user?.withWholesaleInventory && (vehicle.color || vehicle.color_canonical) && (
        <CardRow>
          <span>{colonFormatter(t('components.table.columns.color'))}</span>
          <ColorDot canonical={vehicle.color_canonical} label={vehicle.color} />
        </CardRow>
      )}
      <CardRow>
        <span>{colonFormatter(t('components.table.columns.location'))}</span>
        <span>{vehicle.dealer.shortname}</span>
      </CardRow>
      <CardRow>
        <span>{colonFormatter(t('components.table.columns.miles'))}</span>
        <span>{thousandFormatter(vehicle.odometer)}</span>
      </CardRow>
      <CardRow>
        <span>{colonFormatter(t('components.table.columns.retailPrice'))}</span>
        <span>{costFormatter(vehicle.current_price)}</span>
      </CardRow>
      {tableView === TableView.SALESPERSON && typeof vehicle.additional_down_payment !== 'undefined' && (
        <CardRow>
          <span>{colonFormatter(t('components.table.columns.extraCash'))}</span>
          <span>
            {isExtraCashFormatted
              ? getExtraCashConversionValue(vehicle.additional_down_payment)
              : costFormatter(vehicle.additional_down_payment)}
          </span>
        </CardRow>
      )}
    </Card>
  );
};
